/**
 * Дзвінок, який людина щойно почала з картки (Г2-W1; CALL-03): стан між кліком «Подзвонити» і
 * відповіддю `POST /api/v1/calls`. Рядок дзвінка і блок результату показуються одразу після кліку
 * (`pending`), а коли сервер дав `id` (`ready`), картка переходить на `?call=<id>` і далі читає сам
 * дзвінок (тікет 512e18c4: форма не чекає сервера).
 *
 * Один дзвінок на запис-господар: новий клік на тому самому записі замінює попередній. Стан — у
 * пам'яті вкладки: після перезавантаження дзвінок без результату знайдеться в «Без результату».
 */

import type { CallCreateResponse } from '@clientline/contracts';
import { useSyncExternalStore } from 'react';
import { createTelCall, type TelCall, telCallKey } from './start-call.ts';

export type ActiveCallState = 'pending' | 'ready' | 'failed';

/** Створений дзвінок: `id` і версія для `If-Match` результату. */
export interface CreatedCall {
  id: string;
  version: number;
}

export interface ActiveCall {
  /** `<сутність>:<id>` господаря. */
  host: string;
  /** `Idempotency-Key` створення: ім'я цього кліку, поки `id` ще немає (чернетка, ключ блоку). */
  key: string;
  /** Задача-план дзвінка (`plan` у `POST /calls`); `null` — дзвінок не з задачі. */
  plan: string | null;
  /** Номер, як його набрали (для рядка дзвінка й копіювання). */
  number: string;
  startedAt: number;
  state: ActiveCallState;
  /** `id` дзвінка — коли `ready`. */
  id: string | null;
  /** Версія з відповіді `201` — коли `ready`. */
  version: number | null;
  /** Збій створення: той самий ключ ідемпотентності, тож повтор не дасть другого дзвінка. */
  retry: () => void;
  /**
   * Дочекатися дзвінка («Зберегти» раніше за `id`): запит у дорозі — його відповідь, після збою —
   * новий запит з тим самим ключем (сервер поверне той самий дзвінок, дубля немає).
   */
  created: () => Promise<CreatedCall>;
}

const calls = new Map<string, ActiveCall>();
const listeners = new Set<() => void>();

const hostKey = (host: { entity: string; id: string }) => `${host.entity}:${host.id}`;

function set(call: ActiveCall) {
  calls.set(call.host, call);
  for (const listener of listeners) listener();
}

/** Прибрати дзвінок запису (рядок закрито, результат записано). */
export function clearActiveCall(host: { entity: string; id: string }) {
  if (!calls.delete(hostKey(host))) return;
  for (const listener of listeners) listener();
}

/**
 * Почати запис дзвінка: викликати в обробнику кліку `tel:` одразу після `startCall` (набір не чекає
 * запиту). Відповідь з `id` переводить стан у `ready`, збій — у `failed` з повтором. Повертає першу
 * відповідь (`null` — збій): замаскований номер набирають лише за її `telHref` (SEC-074).
 */
export function beginTelCall(
  call: TelCall,
  number: string,
  now: number = Date.now(),
): Promise<CallCreateResponse | null> {
  const key = telCallKey(call, now);
  const host = hostKey(call.host);
  let request: Promise<CallCreateResponse> | null = null;
  let done: CreatedCall | null = null;
  let failed = false;
  const at = (state: ActiveCallState): ActiveCall => ({
    host,
    key,
    plan: call.plan ?? null,
    number,
    startedAt: now,
    state,
    id: done?.id ?? null,
    version: done?.version ?? null,
    retry: () => void send(),
    created,
  });
  // Відповідь застарілого кліку (на записі вже новий дзвінок) стан не чіпає.
  const current = () => calls.get(host)?.created === created;
  function send(first = false): Promise<CallCreateResponse> {
    failed = false;
    if (first || current()) set(at('pending'));
    const sent = createTelCall(call, key);
    request = sent;
    sent.then(
      (answer) => {
        if (request !== sent) return;
        done = { id: answer.id, version: answer.version };
        if (current()) set(at('ready'));
      },
      () => {
        if (request !== sent) return;
        failed = true;
        if (current()) set(at('failed'));
      },
    );
    return sent;
  }
  function created(): Promise<CreatedCall> {
    if (done) return Promise.resolve(done);
    const sent = failed || !request ? send() : request;
    return sent.then((answer) => ({ id: answer.id, version: answer.version }));
  }
  // Перший запит — викликачу: для замаскованого номера (SEC-074) набір чекає `telHref` відповіді,
  // бо номер сервер бере розкриттям з аудитом. Збій — `null`: стан `failed` уже показує рядок.
  return send(true).then(
    (answer) => answer,
    () => null,
  );
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Дзвінок, почато на цьому записі; `null` — немає. */
export function useActiveCall(host: { entity: string; id: string }): ActiveCall | null {
  const key = hostKey(host);
  return useSyncExternalStore(
    subscribe,
    () => calls.get(key) ?? null,
    () => null,
  );
}
